import { Component, OnDestroy, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { Router, RouterModule } from "@angular/router"
import { StationService } from "../../services/station.service"
import { StationStoreService } from "../../services/station-store.service"
import { Station } from "../../models/station.model"
import { PermissionService } from "../../services/permission.service"
import { AuthService } from "../../services/auth.service"
import { StationContextService } from "../../services/station-context.service"
import { EMPTY, Subject, expand, reduce, catchError, of, takeUntil } from "rxjs"
import {
  stationRoleBadgeClass,
  stationRoleLabel,
} from "../../utils/station-role.util"
import {
  CommonTableComponent,
  TableColumn,
  TableAction,
  TableSortChange,
  TableSortOrder,
} from "../../shared/common-table/common-table.component"
import { PageHeaderComponent } from "../../shared/page-header/page-header.component"
import { MatIconModule } from "@angular/material/icon"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatSelectModule } from "@angular/material/select"
import { SnackbarService } from "../../services/snackbar.service"
import { ConfirmDialogService } from "../../services/confirm-dialog.service"
import { MatDialog } from "@angular/material/dialog"
import {
  StationDeletionAssociatedData,
  StationDeletionInfo,
} from "../../services/station.service"
import { ForceDeleteStationDialogComponent } from "./dialogs/force-delete-station-dialog.component"
import { CoverageCitySelectComponent } from "../../shared/coverage-city-select/coverage-city-select.component"
import { UsCity, formatCityLabel, formatCoverageCitiesLabel } from "../../models/city.model"
import { StationFormComponent } from "./station-form/station-form.component"
import { OwnerService } from "../../services/owner.service"
import { OwnerListItem } from "../../models/filter.model"

@Component({
  selector: "app-manage-stations",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    CommonTableComponent,
    PageHeaderComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    CoverageCitySelectComponent,
  ],
  templateUrl: "./manage-stations.component.html",
  styleUrl: "./manage-stations.component.css",
})
export class ManageStationsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()

  stations: Station[] = []
  loading = false
  currentUserRole = ""
  canCreateNewStation = false
  togglingVisibilityId: number | null = null

  tableColumns: TableColumn[] = []

  tableActions: TableAction[] = [
    {
      icon: "edit",
      label: "Edit station",
      class: "btn-edit",
      callback: (station: Station) => this.navigateToEdit(station),
      show: (station: Station) => this.canEditStation(station),
    },
    {
      icon: "delete",
      label: "Delete station",
      class: "btn-delete",
      callback: (station: Station) => this.deleteStation(station),
      show: (station: Station) => this.canDeleteStation(station),
    },
  ]

  currentPage = 1
  itemsPerPage = 10
  totalItems = 0
  totalPages = 0

  sortBy = "id"
  sortOrder: TableSortOrder = "DESC"

  owners: OwnerListItem[] = []
  loadingOwners = false
  selectedOwnerId: number | "" = ""

  selectedCoverageCities: UsCity[] = []
  userStationsLoadError = ""

  showCoveragePreview = false
  selectedCoverageStation: Station | null = null
  coveragePreviewUrl: string | null = null

  /** Stations whose logo URL failed to load, so the table falls back to the initial. */
  private brokenLogoStationIds = new Set<number>()

  constructor(
    private stationService: StationService,
    private stationStore: StationStoreService,
    private ownerService: OwnerService,
    readonly permissionService: PermissionService,
    private authService: AuthService,
    private stationContext: StationContextService,
    private snackbar: SnackbarService,
    private confirmDialog: ConfirmDialogService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  ngOnInit() {
    this.currentUserRole = this.permissionService.getCurrentRole()
    this.refreshCreatePermission()
    this.buildTableColumns()
    this.permissionService.currentRole$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.currentUserRole = this.permissionService.getCurrentRole()
      this.refreshCreatePermission()
      this.buildTableColumns()
    })
    if (!this.isUserLike()) {
      if (this.permissionService.getBaseRole() === "admin") {
        this.loadOwnersForFilter()
      }
      this.loadStations()
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private buildTableColumns(): void {
    const cols: TableColumn[] = []

    if (this.isAdmin) {
      cols.push({ key: "id", label: "ID", width: "5%", class: "th-id", sortable: true, sortKey: "id" })
    }

    cols.push(
      { key: "call_sign", label: "Call Sign", width: "10%", class: "th-call-sign", sortable: true, sortKey: "call_sign" },
      { key: "name", label: "Station Name", width: "18%", class: "th-name", sortable: true, sortKey: "name" },
    )

    if (this.isAdminOrOwner) {
      cols.push({ key: "is_active", label: "Visible", width: "8%", class: "th-visibility", sortable: false })
    }

    cols.push(
      { key: "user_role", label: "Role", width: "8%", class: "th-role", sortable: false },
      { key: "format", label: "Format", width: "9%", class: "th-format", sortable: true, sortKey: "format" },
      { key: "dial_position", label: "Dial", width: "7%", class: "th-dial", sortable: true, sortKey: "dial_position" },
      {
        key: "coverage_area",
        label: "Coverage",
        width: "14%",
        class: "th-coverage",
        sortable: true,
        sortKey: "coverage_cities",
      },
      { key: "audience", label: "Audience", width: "8%", class: "th-audience", sortable: true, sortKey: "audience" },
    )

    this.tableColumns = cols
  }

  private refreshCreatePermission(): void {
    if (this.permissionService.getBaseRole() === "admin") {
      this.canCreateNewStation = true
      return
    }
    this.canCreateNewStation = this.stationContext
      .getAssignments()
      .some((a) => a.station_role === "owner")
  }

  loadOwnersForFilter(): void {
    this.loadingOwners = true
    this.ownerService.getAllOwners().subscribe({
      next: (owners) => {
        this.owners = owners
        this.loadingOwners = false
      },
      error: () => {
        this.loadingOwners = false
      },
    })
  }

  onOwnerFilterChange(): void {
    this.currentPage = 1
    this.loadStations()
  }

  navigateToAdd(): void {
    if (!this.canCreateStation()) {
      this.snackbar.warning("You do not have permission to add stations.")
      return
    }
    void this.router.navigate(["/managestations/add"])
  }

  navigateToEdit(station: Station): void {
    if (!this.canEditStation(station)) {
      this.snackbar.warning("You do not have permission to edit this station.")
      return
    }
    void this.router.navigate(["/managestations", station.id, "edit"])
  }

  viewCoverageMap(station: Station) {
    if (!station?.coverage_map_url) return
    this.selectedCoverageStation = station
    this.coveragePreviewUrl = station.coverage_map_url
    this.showCoveragePreview = true
  }

  closeCoveragePreview() {
    this.showCoveragePreview = false
    this.selectedCoverageStation = null
    this.coveragePreviewUrl = null
  }

  onUserCoverageCitiesChange(cities: UsCity[]): void {
    if (!this.isUserLike()) return
    this.selectedCoverageCities = cities
    this.currentPage = 1
    this.stations = []
    this.totalItems = 0
    this.totalPages = 0
    this.userStationsLoadError = ""
    if (cities.length === 0) {
      this.loading = false
      return
    }
    this.loadUserStationsByCities()
  }

  loadUserStationsByCities(): void {
    const cityNames = this.selectedCoverageCities.map((city) => city.name).filter(Boolean)
    if (!cityNames.length) {
      this.stations = []
      this.loading = false
      return
    }
    this.loading = true
    this.userStationsLoadError = ""
    const offset = (this.currentPage - 1) * this.itemsPerPage
    this.stationService.getStationsByCities(cityNames, this.itemsPerPage, offset).subscribe({
      next: (response) => {
        this.stations = (response.stations || []).sort((a, b) => a.call_sign.localeCompare(b.call_sign))
        const total = response.pagination?.total ?? 0
        this.totalItems = total
        this.totalPages = total === 0 ? 0 : Math.ceil(total / this.itemsPerPage)
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
          this.currentPage = this.totalPages
          this.loadUserStationsByCities()
          return
        }
        this.loading = false
      },
      error: (error) => {
        this.userStationsLoadError = this.snackbar.apiError(
          error,
          "Failed to load stations for the selected cities. Please try again.",
        )
        this.stations = []
        this.totalItems = 0
        this.totalPages = 0
        this.loading = false
      },
    })
  }

  get userHasSelectedCities(): boolean {
    return this.selectedCoverageCities.length > 0
  }

  get selectedCitiesLabel(): string {
    return this.selectedCoverageCities.map((city) => formatCityLabel(city)).join(", ")
  }

  loadStations() {
    if (this.isUserLike()) {
      if (this.selectedCoverageCities.length > 0) {
        this.loadUserStationsByCities()
      }
      return
    }
    this.loading = true
    const offset = (this.currentPage - 1) * this.itemsPerPage
    this.stationService
      .getStations(this.itemsPerPage, offset, {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ownerId: this.selectedOwnerId === "" ? undefined : Number(this.selectedOwnerId),
      })
      .subscribe({
      next: (response) => {
        this.stations = response.stations || []
        const total = response.pagination?.total ?? 0
        this.totalItems = total
        this.totalPages = total === 0 ? 0 : Math.ceil(total / this.itemsPerPage)
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
          this.currentPage = this.totalPages
          this.loadStations()
          return
        }
        this.loading = false
      },
      error: (error) => {
        this.snackbar.error(this.snackbar.apiError(error, "Failed to load stations. Please try again."))
        this.loading = false
      },
    })
  }

  get paginationStart(): number {
    if (this.totalItems === 0) return 0
    return (this.currentPage - 1) * this.itemsPerPage + 1
  }

  get paginationEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems)
  }

  deleteStation(station: Station) {
    this.stationService.getStationDeletionInfo(station.id).subscribe({
      next: (info) => this.handleStationDeletionInfo(station, info),
      error: (error) => {
        this.snackbar.error(this.snackbar.apiError(error, "Failed to check station deletion status."))
      },
    })
  }

  private handleStationDeletionInfo(station: Station, info: StationDeletionInfo): void {
    const associated = this.resolveAssociatedData(info)
    const hasAssociatedData = this.hasAssociatedDataCounts(associated)

    // Case A — orders/schedules block deletion (force delete not allowed)
    if (info.can_force_delete === false) {
      const ordersCount = info.orders_count ?? 0
      const schedulesCount = info.schedules_count ?? 0
      this.confirmDialog
        .open({
          title: "Cannot Delete Station",
          message:
            `This station has ${ordersCount} orders and ${schedulesCount} scheduled airings. ` +
            `Stations with active orders cannot be deleted to protect financial records.`,
          confirmLabel: "OK",
          hideCancel: true,
          confirmIcon: null,
        })
        .subscribe()
      return
    }

    // Case B — associated data exists; require typed confirmation + force delete
    if (info.can_force_delete === true && hasAssociatedData) {
      this.dialog
        .open(ForceDeleteStationDialogComponent, {
          width: "min(520px, 92vw)",
          maxWidth: "92vw",
          panelClass: "app-confirm-dialog",
          autoFocus: "first-tabbable",
          data: {
            stationName: station.name,
            associatedData: associated,
          },
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (!confirmed) return
          this.performForceDelete(station)
        })
      return
    }

    // Case C — no associated data; normal delete
    this.confirmDialog
      .open({
        title: "Delete Station",
        message: `Are you sure you want to delete ${station.name}?`,
        confirmLabel: "Delete Station",
        cancelLabel: "Cancel",
        confirmIcon: null,
      })
      .subscribe((confirmed) => {
        if (!confirmed) return
        this.performNormalDelete(station)
      })
  }

  private resolveAssociatedData(info: StationDeletionInfo): StationDeletionAssociatedData {
    if (info.associated_data) {
      return info.associated_data
    }
    return {
      rates_count: info.rates_count,
      clocks_count: info.clocks_count,
      lockouts_count: info.lockouts_count,
      order_rules_count: info.order_rules_count,
      station_users_count: info.station_users_count,
      ratings_count: info.ratings_count,
      recording_approvals_count: info.recording_approvals_count,
    }
  }

  private hasAssociatedDataCounts(data: StationDeletionAssociatedData): boolean {
    return (
      (data.rates_count ?? 0) > 0 ||
      (data.clocks_count ?? 0) > 0 ||
      (data.lockouts_count ?? 0) > 0 ||
      (data.order_rules_count ?? 0) > 0 ||
      (data.station_users_count ?? 0) > 0 ||
      (data.ratings_count ?? 0) > 0 ||
      (data.recording_approvals_count ?? 0) > 0
    )
  }

  private performForceDelete(station: Station): void {
    this.stationService.forceDeleteStation(station.id).subscribe({
      next: () => {
        this.snackbar.success(
          `Station ${station.name} and all associated data has been permanently deleted.`,
        )
        this.stationStore.invalidateAndRefresh().subscribe({ error: () => {} })
        this.stationService.clearFilterStationsCache()
        void this.router.navigate(["/managestations"])
        this.loadStations()
      },
      error: (error) => {
        this.snackbar.error(this.snackbar.apiError(error, "Failed to delete station. Please try again."))
      },
    })
  }

  private performNormalDelete(station: Station): void {
    this.stationService.deleteStation(station.id).subscribe({
      next: () => {
        this.snackbar.success(`Station "${station.name}" was deleted.`)
        this.stationStore.invalidateAndRefresh().subscribe({ error: () => {} })
        this.stationService.clearFilterStationsCache()
        this.loadStations()
      },
      error: (error) => {
        this.snackbar.error(
          this.stationService.formatDeleteError(error, "Failed to delete station. Please try again."),
        )
      },
    })
  }

  goToPage(page: number) {
    if (this.totalPages === 0) return
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page
      this.loadStations()
    }
  }

  onSortChange(change: TableSortChange): void {
    this.sortBy = change.sortBy
    this.sortOrder = change.sortOrder
    this.currentPage = 1
    this.loadStations()
  }

  getPaginationPages(): number[] {
    const pages: number[] = []
    const maxVisible = 5
    if (!this.totalPages || this.totalPages <= 0) return pages
    if (this.totalPages <= maxVisible) {
      for (let i = 1; i <= this.totalPages; i++) pages.push(i)
    } else {
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, start + maxVisible - 1)
      for (let i = start; i <= end; i++) pages.push(i)
    }
    return pages
  }

  formatCoverageCity(station: Station): string {
    if (station.coverage_cities?.length) {
      return formatCoverageCitiesLabel(station.coverage_cities)
    }
    if (!station?.coverage_area) return "-"
    if (station.coverage_state_code && !station.coverage_area.includes(",")) {
      return `${station.coverage_area}, ${station.coverage_state_code}`
    }
    return station.coverage_area
  }

  /** First coverage city, shown in full; the rest go in the "+n more" tooltip. */
  firstCoverageCity(station: Station): string {
    const first = station.coverage_cities?.[0]
    return first ? formatCityLabel(first) : ""
  }

  getCitiesTooltip(station: Station): string {
    const rest = station.coverage_cities?.slice(1) ?? []
    return rest.map((city) => formatCityLabel(city)).join(", ")
  }

  hasStationLogo(station: Station): boolean {
    return !!station.logo_url && !this.brokenLogoStationIds.has(station.id)
  }

  onStationLogoError(station: Station): void {
    this.brokenLogoStationIds.add(station.id)
  }

  stationInitial(station: Station): string {
    return station.call_sign?.charAt(0) || station.name?.charAt(0) || "?"
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString()
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
  }

  canCreateStation(): boolean {
    return this.canCreateNewStation
  }

  canEditStation(station: Station): boolean {
    return this.permissionService.canEditStationForRole(this.resolveStationRole(station))
  }

  canDeleteStation(station: Station): boolean {
    return this.permissionService.canDeleteStationForRole(this.resolveStationRole(station))
  }

  hasAnyRowActions(): boolean {
    return this.stations.some((s) => this.canEditStation(s) || this.canDeleteStation(s))
  }

  resolveStationRole(station: Station): string {
    return (
      station.user_role ??
      this.stationContext.getRoleForStation(station.id) ??
      this.permissionService.getCurrentRole()
    )
  }

  roleLabel(station: Station): string {
    return stationRoleLabel(this.resolveStationRole(station))
  }

  roleBadgeClass(station: Station): string {
    return stationRoleBadgeClass(this.resolveStationRole(station))
  }

  trackByStationId(_index: number, station: Station): number {
    return station.id
  }

  trackByPage(_index: number, page: number): number {
    return page
  }

  get isAdmin(): boolean {
    // Admin is a platform-wide role, so the login (base) role is the right source
    // here -- and it is the same source lines 133/180 of this component already use.
    return this.permissionService.getBaseRole() === "admin"
  }

  get isAdminOrOwner(): boolean {
    return this.permissionService.isAdmin() || this.permissionService.isOwner()
  }

  toggleStationVisibility(station: Station, event: Event): void {
    const checkbox = event.target as HTMLInputElement
    const newValue = checkbox.checked ? 1 : 0
    this.togglingVisibilityId = station.id

    this.stationService.updateStationVisibility(station.id, newValue).subscribe({
      next: () => {
        station.is_active = newValue
        this.togglingVisibilityId = null
        this.snackbar.success(
          newValue === 1
            ? `${station.name} is now visible to advertisers.`
            : `${station.name} is now hidden from advertisers.`,
        )
      },
      error: () => {
        checkbox.checked = !checkbox.checked
        this.togglingVisibilityId = null
        this.snackbar.error("Failed to update station visibility.")
      },
    })
  }

  isUserLike(): boolean {
    return this.permissionService.isAdvertiserUserOnly()
  }
}
